import { Link, useNavigate } from '@tanstack/react-router'
import { useRef, useState, type FormEvent } from 'react'

import { newIdempotencyKey } from '../../app/ai-os'
import { ApiFailure } from '../../app/errors'
import type { ProjectImportCreated } from '../../app/project-imports'
import { useCreateProjectImport } from '../../app/queries'
import { Button } from '../../components/button'
import { Card } from '../../components/card'
import { Page, PageHeader } from '../../components/page'
import { t } from '../../copy'
import { ProjectImportAssigneePicker } from './assignee-picker'

export function ProjectImportCreateScreen() {
  const navigate = useNavigate()
  const create = useCreateProjectImport()
  const [name, setName] = useState('')
  const [draft, setDraft] = useState<ProjectImportCreated | null>(null)
  const [error, setError] = useState('')
  const issueKey = useRef<string | null>(null)

  async function createDraft(event: FormEvent) {
    event.preventDefault()
    setError('')
    try {
      if (issueKey.current === null) issueKey.current = newIdempotencyKey()
      const created = await create.mutateAsync({ name: name.trim(), idempotencyKey: issueKey.current })
      issueKey.current = null
      setDraft(created)
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause.message : t('error.generic'))
    }
  }

  return <Page width="narrow">
    <PageHeader title={t('project_import.wizard.title')} subtitle={t('project_import.wizard.subtitle')} breadcrumb={<Link to="/projects">{t('nav.project')}</Link>} />
    {!draft ? <Card className="max-w-[560px]">
      <form onSubmit={(event) => void createDraft(event)} className="flex flex-col gap-npc-grid-sm" data-testid="project-import-form">
        <label className="flex flex-col gap-npc-gap-xs" htmlFor="project-import-name"><span className="text-npc-caption text-npc-ink-soft">{t('project_import.wizard.name')}</span><input id="project-import-name" data-testid="project-import-name" value={name} onChange={(event) => setName(event.target.value)} className="min-h-11 border border-npc-border bg-white px-npc-grid-sm text-npc-body-sm outline-none focus:border-npc-blue" autoComplete="off" /></label>
        {error ? <ErrorLine text={error} /> : null}
        <div><Button type="submit" variant="primary" disabled={create.isPending || name.trim() === ''} data-testid="project-import-submit">{create.isPending ? t('project_import.wizard.submitting') : t('project_import.wizard.submit')}</Button></div>
      </form>
    </Card> : <ProjectImportAssigneePicker importId={draft.import.id} draftReady onAssigned={async (assigned) => {
      await navigate({ to: '/projects/import/$importId', params: { importId: draft.import.id }, state: { invitation: assigned.invitation ?? null } as never })
    }} />}
  </Page>
}

function ErrorLine({ text }: { text: string }) {
  return <p className="m-0 text-npc-body-sm text-npc-red-ink" data-testid="project-import-error">{text}</p>
}
